// Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.


import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import * as Sentry from '@sentry/browser'

import {
  ExpandableOverviewItem,
  SpanWithData,
  SvgIcon
} from '../'

import { Payslip } from './Payslip'

import {
  showAsCurrency,
  decodeBase64
} from '../../utils'


export default class CreditApplicationIncome extends PureComponent {
  static propTypes = {
    employmentCommencedAt: PropTypes.string,
    selectablePayslips: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string
      })
    ),
    selectedPayslips: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string
      })
    ),
    missingPayslipDates: PropTypes.arrayOf(PropTypes.string),
    client: PropTypes.shape({
      id: PropTypes.string,
      identity: PropTypes.shape({
        id: PropTypes.string.isRequired
      }),
      employmentCommencedAt: PropTypes.string,
      payrollcycle: PropTypes.shape({
        cycle: PropTypes.string
      })
    }).isRequired,
    updateCreditApplication: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    locked: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  state = {
    selectActionInProgress: false,
    selectablePayslipRefs: [],
    selectedPayslipRefs: [],
    currentProcessingPayslipId: null
  }

  handlePayslipSelectClick = (newPayslipId) => {
    this.setState(
      { selectActionInProgress: true, currentProcessingPayslipId: newPayslipId },
      () => {
        const payslips = this.props.selectedPayslips.map(payslip => decodeBase64(payslip.id))  // get current selected payslips as array of uuid's
        payslips.push(decodeBase64(newPayslipId))  // add new payslip to array

        this.props.updateCreditApplication({ selectedPayslips: payslips })
        .then(() => {
          this.setState({ selectActionInProgress: false })
        })
        .catch(error => {
          Sentry.captureMessage(`Failed to save selected payslip - ${error}`)
          this.setState({ selectActionInProgress: false })
        })
      }
    )
  }

  handlePayslipUnselectClick = (payslipId) => {
    this.setState(
      { selectActionInProgress: true, currentProcessingPayslipId: payslipId },
      () => {
        const index = this.props.selectedPayslips.findIndex(payslip => payslip.id === payslipId)
        const payslips = this.props.selectedPayslips.map(payslip => decodeBase64(payslip.id))  // get current selected payslips as array of uuid's
        payslips.splice(index, 1)  // remove chosen payslip from array

        if (index > -1) {
          this.props.updateCreditApplication({ selectedPayslips: payslips })
          .then(() => this.setState({ selectActionInProgress: false }))
          .catch(error => {
            Sentry.captureMessage(`Failed to save selected payslip - ${error}`)
            this.setState({ selectActionInProgress: false })
          })
        } else {
          Sentry.captureMessage('Payslip to unselect not found')
          this.setState({ selectActionInProgress: false })
        }
      }
    )
  }

  render() {
    const { employmentCommencedAt, client, selectablePayslips, selectedPayslips, missingPayslipDates, open, locked, onClick, className } = this.props
    const threePayslipAttached = selectedPayslips.length === 3

    let averageGross = selectedPayslips.reduce((prev, cur) => prev + parseFloat(cur.totalGross), 0) / selectedPayslips.length
    averageGross = showAsCurrency('R', averageGross)

    return (
      <ExpandableOverviewItem
        title='Income'
        summary={`${selectedPayslips.length} payslips${threePayslipAttached ? `; Gross average: ${averageGross}` : `. Requires ${3 - selectedPayslips.length} more`}`}
        iconSymbol={threePayslipAttached ? 'check' : 'warning'}
        iconColour={threePayslipAttached ? 'success' : 'warning'}
        open={open}
        onClick={onClick}
        className={className}>

        <div className='slds-grid slds-size_1-of-2'>
          <div className='slds-size_1-of-2'>
            Employed since:
          </div>
          <div className='slds-size_1-of-2 slds-text-color_weak'>
            {employmentCommencedAt ? moment(employmentCommencedAt).format('D MMMM YYYY') : 'Unknown'}
          </div>
        </div>
        <div className='slds-grid slds-size_1-of-2'>
          <div className='slds-size_1-of-2'>
            Paid (frequency):
          </div>
          <div className='slds-size_1-of-2 slds-text-color_weak'>
            <SpanWithData
              forField='PayrollCycleCycle'
              searchKey={client.payrollcycle.cycle}
            />
          </div>
        </div>
        <div className='slds-grid slds-size_1-of-2'>
          <div className='slds-size_1-of-2'>
            Gross average:
          </div>
          <div className='slds-size_1-of-2 slds-text-color_weak'>
            {averageGross}
          </div>
        </div>
        <div className='slds-m-top_large slds-m-bottom_medium'>
          <div className='u-font-size-larger'>
            <b>Available payslips</b>
          </div>
          {selectablePayslips.length > 0
            ? selectablePayslips.map(payslip => (
                <Payslip
                  id={payslip.id}
                  identityId={client.identity.id}
                  buttonLabel={this.state.selectActionInProgress &&
                    this.state.currentProcessingPayslipId === payslip.id
                    ? 'Selecting...'
                    : 'Select'
                  }
                  buttonDisabled={locked || this.state.selectActionInProgress}
                  onClick={() => this.handlePayslipSelectClick(payslip.id)}
                  key={`payslip_${payslip.id}`}
                />
              ))
            : 'None'
          }
          <div className='u-font-size-larger slds-m-top_large'>
            <b>Selected payslips</b>
          </div>
          {selectedPayslips.length > 0
            ? selectedPayslips.map(payslip => (
                <Payslip
                  id={payslip.id}
                  identityId={client.identity.id}
                  buttonLabel={this.state.selectActionInProgress &&
                    this.state.currentProcessingPayslipId === payslip.id
                    ? 'Unselecting...'
                    : 'Unselect'
                  }
                  buttonDisabled={locked || this.state.selectActionInProgress}
                  onClick={() => this.handlePayslipUnselectClick(payslip.id)}
                  key={`payslip_${payslip.id}`}
                />
              ))
            : 'None'
          }
          {missingPayslipDates.length > 0 &&
            <div className='slds-m-top_x-large slds-size_11-of-12'>
              <SvgIcon
                variant='utility'
                symbol='info'
                colour='light'
                size='small'
                className='slds-m-right_x-small'
              />
              Clients may provide payslips from the following pay dates:
              <div className='slds-text-color_weak u-font-size-larger slds-m-top_xx-small slds-m-bottom_x-large'>
                {missingPayslipDates.map((date, index) => (
                  index === 0
                    ? moment(date).format('DD MMMM YYYY')
                    : <span key={`missingPayslipDates_${date}`}>
                        ,&nbsp;&nbsp;{moment(date).format('DD MMMM YYYY')}
                      </span>
                ))}
              </div>
            </div>
          }
        </div>
      </ExpandableOverviewItem>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/creditapplication-income/index.js